// Systém → Change log.
// Co všechno se v systému měnilo: admin, klientská aplikace, web, klub.
//
// Záznamy se plní SAMY z gitu při každém nasazení (scripts/changelog-git.mjs →
// /api/changelog-import). Zpráva commitu je nadpis řádku, proto se píše česky
// a lidsky. Ručně se dá text přepsat a přidat změna, která přes git nešla
// (nastavení v Make, úprava webu v editoru, změna v Notionu).
//
// Vzhled záměrně kopíruje Log změn webu (/web/zmeny): stejná hlavička, karta
// s filtry, tabulka s barevným proužkem a rozbalovacím detailem.
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  History, Loader2, Plus, Save, Search, ChevronDown, ChevronUp, Trash2, EyeOff, Eye, X,
  Server, Smartphone, Globe, HeartHandshake, Sparkles, Wrench, Pencil, Users, GitCommit,
} from "lucide-react";
import { apiFetch } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, ErrorNote } from "../../components/ui.jsx";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { getCurrentUser } from "../../lib/auth.js";

const OBLASTI_META = {
  "Admin": { icon: Server, chip: "bg-slate-100 text-slate-700 border-slate-200", pruh: "bg-slate-400" },
  "Klientská aplikace": { icon: Smartphone, chip: "bg-brand-100 text-brand-700 border-brand-200", pruh: "bg-brand-500" },
  "Web": { icon: Globe, chip: "bg-sky-100 text-sky-700 border-sky-200", pruh: "bg-sky-500" },
  "Klub": { icon: HeartHandshake, chip: "bg-emerald-100 text-emerald-700 border-emerald-200", pruh: "bg-emerald-500" },
};
const TYPY_META = {
  "Nová funkce": { icon: Sparkles, chip: "border-emerald-300 bg-emerald-100 text-emerald-800" },
  "Oprava": { icon: Wrench, chip: "border-rose-300 bg-rose-100 text-rose-800" },
  "Úprava": { icon: Pencil, chip: "border-amber-300 bg-amber-100 text-amber-800" },
  "Odstranění": { icon: Trash2, chip: "border-slate-300 bg-slate-100 text-slate-700" },
};
const oblastMeta = (o) => OBLASTI_META[o] || OBLASTI_META.Admin;
const typMeta = (t) => TYPY_META[t] || TYPY_META["Úprava"];

function fmtDatum(iso) {
  if (!iso) return "";
  const [r, m, d] = String(iso).split("-");
  if (!r) return iso;
  return `${Number(d)}. ${Number(m)}. ${r}`;
}
function dnesIso() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default function ChangeLog() {
  const user = getCurrentUser();
  const jeOwner = String(user?.role || "").toLowerCase() === "owner";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [otevrene, setOtevrene] = useState(null);
  const [upravovane, setUpravovane] = useState(null);   // id právě upravovaného řádku
  const [koncept, setKoncept] = useState(null);          // rozpracovaná úprava
  const [novy, setNovy] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [hledej, setHledej] = useState("");
  const [filtrOblast, setFiltrOblast] = useState("vse");
  const [filtrTyp, setFiltrTyp] = useState("vse");
  const [ukazSkryte, setUkazSkryte] = useState(false);
  const detailRefs = useRef({});

  const load = async () => {
    try {
      const r = await apiFetch("/api/system-changelog");
      const j = await r.json();
      if (j.ok) { setData(j); setErr(""); } else setErr(j.error || "Načtení se nepovedlo.");
    } catch (e) { setErr(String(e.message || e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Rozbalený řádek se posune do viditelné části, ať detail není pod okrajem.
  useEffect(() => {
    if (!otevrene) return;
    const el = detailRefs.current[otevrene];
    if (el && el.scrollIntoView) {
      const t = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "nearest" }), 60);
      return () => clearTimeout(t);
    }
  }, [otevrene]);

  const post = async (body) => {
    setSaving(true);
    try {
      const r = await apiFetch("/api/system-changelog", { method: "POST", body: JSON.stringify(body) });
      const j = await r.json();
      setSaving(false);
      if (!j.ok) { setErr(j.error || "Akce se nepovedla."); return false; }
      setErr("");
      await load();
      return true;
    } catch (e) { setSaving(false); setErr(String(e.message || e)); return false; }
  };

  const oblasti = data?.oblasti || [];
  const typy = data?.typy || [];
  const dopady = data?.dopady || [];
  const vsechny = data?.zaznamy || [];

  const videt = useMemo(() => {
    let z = vsechny;
    if (!ukazSkryte) z = z.filter((x) => !x.skryto);
    if (filtrOblast !== "vse") z = z.filter((x) => x.oblast === filtrOblast);
    if (filtrTyp !== "vse") z = z.filter((x) => x.typ === filtrTyp);
    const h = hledej.trim().toLowerCase();
    if (h) z = z.filter((x) => `${x.nadpis} ${x.popis} ${x.autor}`.toLowerCase().includes(h));
    return z;
  }, [vsechny, ukazSkryte, filtrOblast, filtrTyp, hledej]);

  const skrytych = vsechny.filter((x) => x.skryto).length;
  const bezPopisu = vsechny.filter((x) => !x.skryto && !String(x.popis || "").trim()).length;

  const prepnout = (id) => { setOtevrene((x) => (x === id ? null : id)); setUpravovane(null); setKoncept(null); };
  const zacniUpravu = (z) => { setUpravovane(z.id); setKoncept({ ...z }); };
  const ulozUpravu = async () => {
    if (!koncept) return;
    if (await post({ action: "upravit", id: koncept.id, nadpis: koncept.nadpis, popis: koncept.popis, oblast: koncept.oblast, typ: koncept.typ, dopad: koncept.dopad, datum: koncept.datum })) {
      setUpravovane(null); setKoncept(null);
    }
  };
  const ulozNovy = async () => {
    if (!novy?.nadpis?.trim()) { setErr("Nadpis je povinný."); return; }
    if (await post({ action: "pridat", ...novy })) setNovy(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-16 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <Stranka>
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title || ""}
        description={confirmState?.description}
        confirmLabel={confirmState?.confirmLabel || "Potvrdit"}
        variant="danger"
        onConfirm={() => { const fn = confirmState?.onConfirm; setConfirmState(null); if (fn) fn(); }}
        onCancel={() => setConfirmState(null)}
      />

      <GradientHeader
        icon={History}
        title="Change log"
        napoveda="Co se kdy změnilo v adminu, klientské aplikaci, na webu a v klubu. Záznamy přibývají samy při nasazení, ruční záznam přidáš plusem v hlavičce."
        akce={[{
          klic: "rucni",
          ikona: Plus,
          popis: "Přidat ruční záznam",
          onClick: () => setNovy({ datum: dnesIso(), oblast: "Admin", typ: "Úprava", dopad: "Owner a manager", nadpis: "", popis: "" }),
          disabled: !!novy,
        }]}
      />

      {err && <ErrorNote>{err}</ErrorNote>}

      {/* Pravidlo, ať je jasné, proč se sem má psát a co je čí práce. */}
      <div className="flex items-start gap-3 rounded-2xl border border-violet-200 bg-violet-50 p-4">
        <GitCommit className="mt-0.5 w-5 h-5 shrink-0 text-violet-600" />
        <div className="min-w-0 text-sm text-violet-900">
          <div className="font-semibold">Každá změna v systému musí být tady</div>
          <div className="mt-1 text-violet-800">
            Změny v kódu se zapisují samy při nasazení, takže stačí psát zprávy commitů česky a srozumitelně.
            Co přes kód nejde (scénář v Make, úprava webu v editoru, změna v Notionu), zapiš ručně tlačítkem nahoře.
          </div>
          {bezPopisu > 0 && (
            <div className="mt-1.5 text-violet-700">
              {bezPopisu === 1 ? "Jeden záznam nemá popis" : `${bezPopisu} záznamů nemá popis`}. Rozbal řádek a doplň ho, ať je změna srozumitelná i za rok.
            </div>
          )}
        </div>
      </div>

      {/* Filtry */}
      <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input type="text" value={hledej} onChange={(e) => setHledej(e.target.value)}
            placeholder="Hledat v záznamech" className="field pl-9" />
        </div>
        <select value={filtrOblast} onChange={(e) => setFiltrOblast(e.target.value)} className="field">
          <option value="vse">Všechny oblasti</option>
          {oblasti.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={filtrTyp} onChange={(e) => setFiltrTyp(e.target.value)} className="field">
          <option value="vse">Všechny typy</option>
          {typy.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="flex shrink-0 items-center gap-2 px-1 text-xs">
          <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 font-semibold text-slate-700">
            {videt.length} z {vsechny.length}
          </span>
          {skrytych > 0 && (
            <button type="button" onClick={() => setUkazSkryte((v) => !v)}
              className="btn btn-secondary">
              {ukazSkryte ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {ukazSkryte ? "Skrýt skryté" : `${skrytych} skrytých`}
            </button>
          )}
          {saving && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>
      </div>

      {/* Ruční záznam */}
      {novy && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-800">Ruční záznam</span>
            <span className="flex-1" />
            <button type="button" onClick={() => setNovy(null)} className="p-1 text-slate-400 hover:text-slate-700">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid gap-2 sm:grid-cols-4">
            <input type="date" value={novy.datum} onChange={(e) => setNovy({ ...novy, datum: e.target.value })} className="field" />
            <select value={novy.oblast} onChange={(e) => setNovy({ ...novy, oblast: e.target.value })} className="field">
              {oblasti.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <select value={novy.typ} onChange={(e) => setNovy({ ...novy, typ: e.target.value })} className="field">
              {typy.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={novy.dopad} onChange={(e) => setNovy({ ...novy, dopad: e.target.value })} className="field">
              {dopady.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <input value={novy.nadpis} onChange={(e) => setNovy({ ...novy, nadpis: e.target.value })}
            placeholder="Co se změnilo, jednou větou" className="field" />
          <textarea value={novy.popis} onChange={(e) => setNovy({ ...novy, popis: e.target.value })} rows={3}
            placeholder="Detail: proč se to měnilo a co si má kdo pohlídat" className="field" />
          <button type="button" onClick={ulozNovy} disabled={saving}
            className="btn btn-primary">
            <Save className="w-4 h-4" /> Uložit záznam
          </button>
        </div>
      )}

      {/* Seznam */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="hidden items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500 sm:flex">
          <span className="w-4" />
          <span className="w-24 shrink-0">Datum</span>
          <span className="w-44 shrink-0">Oblast</span>
          <span className="flex-1">Změna</span>
          <span className="w-36 shrink-0 text-right">Typ</span>
        </div>

        {videt.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">
            {vsechny.length === 0
              ? "Zatím tu nic není. Záznamy z gitu se doplní při nejbližším nasazení."
              : "Žádný záznam neodpovídá filtru."}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {videt.map((z) => {
              const ob = oblastMeta(z.oblast);
              const ty = typMeta(z.typ);
              const ObIkona = ob.icon;
              const TyIkona = ty.icon;
              const rozbaleno = otevrene === z.id;
              const edituje = upravovane === z.id;
              const d = edituje ? koncept : z;

              return (
                <div key={z.id} ref={(el) => { detailRefs.current[z.id] = el; }}
                  className={`relative scroll-mt-24 ${rozbaleno ? "bg-slate-50/60" : ""} ${z.skryto ? "opacity-60" : ""}`}>
                  <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${ob.pruh}`} />

                  <button type="button" onClick={() => prepnout(z.id)}
                    className="flex w-full items-center gap-3 px-4 py-3 pl-5 text-left transition hover:bg-slate-50">
                    <span className="shrink-0 text-slate-400">
                      {rozbaleno ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </span>

                    <span className="w-24 shrink-0 text-sm font-semibold tabular-nums text-slate-700">{fmtDatum(d.datum)}</span>

                    <span className={`hidden w-44 shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold sm:inline-flex ${ob.chip}`}>
                      <ObIkona className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{d.oblast}</span>
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold text-slate-900">{d.nadpis || "(bez nadpisu)"}</span>
                      <span className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                        <Users className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                        <span className="truncate">{d.dopad}</span>
                        {z.zdroj === "rucne" && (
                          <>
                            <span aria-hidden className="w-px h-3 bg-current opacity-40" />
                            <span className="truncate">ručně</span>
                          </>
                        )}
                        {z.skryto && (
                          <>
                            <span aria-hidden className="w-px h-3 bg-current opacity-40" />
                            <span className="truncate">skryto</span>
                          </>
                        )}
                      </span>
                    </span>

                    <span className="flex w-36 shrink-0 justify-end">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${ty.chip}`}>
                        <TyIkona className="w-3.5 h-3.5" /> {d.typ}
                      </span>
                    </span>
                  </button>

                  {rozbaleno && (
                    <div className="border-t border-slate-200 bg-white px-4 py-4 pl-5">
                      {!edituje ? (
                        <div className="space-y-3">
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Co se změnilo</div>
                            <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
                              {z.popis || "Bez popisu. Doplň ho tlačítkem Upravit, ať je změna srozumitelná i za rok."}
                            </p>
                          </div>

                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span>{z.zdroj === "git" ? "Zapsáno z gitu" : "Ruční záznam"}</span>
                            {z.autor && (<><span aria-hidden className="w-px h-3 bg-current opacity-40" /><span>{z.autor}</span></>)}
                            {z.repo && (<><span aria-hidden className="w-px h-3 bg-current opacity-40" /><span>{z.repo}</span></>)}
                            {z.sha && (<><span aria-hidden className="w-px h-3 bg-current opacity-40" /><span className="font-mono">{z.sha.substring(0, 8)}</span></>)}
                          </div>

                          {z.soubory.length > 0 && (
                            <details className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                              <summary className="cursor-pointer select-none font-semibold text-slate-700">
                                Změněné soubory ({z.soubory.length})
                              </summary>
                              <div className="mt-1.5 space-y-0.5 font-mono leading-relaxed break-all">
                                {z.soubory.map((f) => <div key={f}>{f}</div>)}
                              </div>
                            </details>
                          )}

                          <div className="flex flex-wrap items-center gap-2 pt-1">
                            <button type="button" onClick={() => zacniUpravu(z)}
                              className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                              Upravit
                            </button>
                            <button type="button" onClick={() => post({ action: "skryt", id: z.id, skryto: !z.skryto })}
                              className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                              {z.skryto ? "Odkrýt" : "Skrýt"}
                            </button>
                            {jeOwner && z.zdroj === "rucne" && (
                              <button type="button"
                                onClick={() => setConfirmState({
                                  title: "Smazat ruční záznam?",
                                  description: <>Záznam „{z.nadpis}" se nenávratně smaže.</>,
                                  confirmLabel: "Smazat",
                                  onConfirm: () => post({ action: "smazat", id: z.id }),
                                })}
                                className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-medium text-rose-600 hover:border-rose-300">
                                <Trash2 className="w-3.5 h-3.5" /> Smazat
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="grid gap-2 sm:grid-cols-4">
                            <input type="date" value={koncept.datum} onChange={(e) => setKoncept({ ...koncept, datum: e.target.value })} className="field" />
                            <select value={koncept.oblast} onChange={(e) => setKoncept({ ...koncept, oblast: e.target.value })} className="field">
                              {oblasti.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                            <select value={koncept.typ} onChange={(e) => setKoncept({ ...koncept, typ: e.target.value })} className="field">
                              {typy.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <select value={koncept.dopad} onChange={(e) => setKoncept({ ...koncept, dopad: e.target.value })} className="field">
                              {dopady.map((x) => <option key={x} value={x}>{x}</option>)}
                            </select>
                          </div>
                          <input value={koncept.nadpis} onChange={(e) => setKoncept({ ...koncept, nadpis: e.target.value })}
                            placeholder="Co se změnilo, jednou větou" className="field" />
                          <textarea value={koncept.popis} onChange={(e) => setKoncept({ ...koncept, popis: e.target.value })} rows={4}
                            placeholder="Popiš lidsky, co se změnilo a proč" className="field" />
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={ulozUpravu} disabled={saving}
                              className="btn btn-primary">
                              <Save className="w-4 h-4" /> Uložit
                            </button>
                            <button type="button" onClick={() => { setUpravovane(null); setKoncept(null); }}
                              className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                              Zrušit
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Stranka>
  );
}
